import { Button } from '@mui/material';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Prompt, useHistory } from 'react-router-dom';
import { PhoneWithControlAndPrefixInput, SelectWithControl, TextInputWithControl } from 'src/components';
import { Loading } from 'src/components/Loading';
import { SCRoutes } from 'src/config';
import { getCitiesListSelector } from 'src/features/appSettings';
import { useRolesMapperSelect } from 'src/hooks';
import { NullableValue, SimpleSelectDataItem } from 'src/types';
import { useCreateUserForm, useSubmitNewUserCreation } from './hooks';
import { CreateNewUserInputs } from './users.types';

type Props = {
  testID?: string;
};

export const DashboardUsersCreate: React.FC<Props> = ({ testID = 'DashboardUsersCreate' }) => {
  const { t } = useTranslation();

  const { push } = useHistory();
  const { handleSubmit, errors, isDirty, control } = useCreateUserForm();
  const { userId, error, isLoading, submitNewUserCreation } = useSubmitNewUserCreation();
  const { rolesMapperSelect } = useRolesMapperSelect();

  const onSubmit = useCallback(
    (data: CreateNewUserInputs) => {
      if (data.role === 'SUPPERADMIN') {
        const result = prompt(t('dashboard-user-create.confirm-supperadmin'));

        if (result !== 'no') {
          return;
        }
      }

      submitNewUserCreation(data);
    },
    [submitNewUserCreation, t],
  );

  const cities = useSelector(getCitiesListSelector);

  useEffect(() => {
    if (userId) {
      push(SCRoutes.dashboard.users.edit.replace(':id', userId));
    }
  }, [userId, push, t]);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      <Prompt when={isDirty} message={t('dashboard-user-create.confirm-exit-without-update')} />
      <div className="bg-white min-h-screen px-4">
        <div data-testid={testID} className="mx-4">
          <div
            className="text-xl text-gray-600"
            dangerouslySetInnerHTML={{
              __html: t('dashboard-user-create.page-title'),
            }}
          />
        </div>

        <div className="w-2/3 m-auto">
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <TextInputWithControl
                  control={control}
                  name="firstName"
                  placeholder={t('dashboard-user-create.form-first-name')}
                  error={errors.firstName}
                  showUpLabel
                />
              </div>
              <div>
                <TextInputWithControl
                  control={control}
                  name="lastName"
                  placeholder={t('dashboard-user-create.form-last-name')}
                  error={errors.lastName}
                  showUpLabel
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <PhoneWithControlAndPrefixInput
                  control={control}
                  name="tel"
                  placeholder={t('dashboard-user-create.form-tel')}
                  error={errors?.tel}
                  showUpLabel
                  copyNumbersOnly={false}
                />
              </div>

              <div>
                <TextInputWithControl
                  control={control}
                  name="email"
                  placeholder={t('dashboard-user-create.form-email')}
                  error={errors.email}
                  showUpLabel
                  type="email"
                  autocomplete="off"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <TextInputWithControl
                  control={control}
                  name="password"
                  placeholder={t('dashboard-user-create.form-password')}
                  error={errors.password}
                  showUpLabel
                  type="password"
                  autocomplete="off"
                />
              </div>
              <div>
                <TextInputWithControl
                  control={control}
                  name="passwordConfirmation"
                  placeholder={t('dashboard-user-create.form-password-confirmation')}
                  error={errors.passwordConfirmation}
                  showUpLabel
                  type="password"
                  autocomplete="off"
                />
              </div>
            </div>

            <div>
              <SelectWithControl
                control={control}
                name="role"
                data={rolesMapperSelect}
                placeholder={t('dashboard-user-create.form-role')}
                error={errors.role}
                showUpLabel
              />
            </div>

            <div>
              <SelectWithControl
                control={control}
                name="city"
                getSelectedId={(data) => {
                  if (!data) return data;

                  if (typeof data === 'string') return data;

                  if ((data as SimpleSelectDataItem).value) {
                    return (data as SimpleSelectDataItem).value;
                  }

                  return data as NullableValue;
                }}
                data={cities ?? []}
                placeholder={t('dashboard-user-create.form-city')}
                error={errors.city}
                showUpLabel
              />
            </div>

            <div className="mt-4">
              {!!error && <div className="text-red-600 text-center">{t(error.message)}</div>}
              <div className="flex justify-end w-full">
                <Button variant="contained" color="primary" type="submit">
                  {t('dashboard-user-create.form-save')}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};
